import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
    Container,
    Button,
    Grid,
    CircularProgress,
    TextField,
    FormControlLabel, Checkbox
} from '@mui/material';
import { Alert } from '@mui/material';
import {useParams, useHistory} from 'react-router-dom';
import {useForm, Controller} from 'react-hook-form';
import {
    GrantDetailService,
    GasCountryService,
    EmailTemplateService,
    LanguageService,
    PrincipalRecipientsService,
    ReportingGroupService
} from '../shared/service';
import ValidationError from "../../shared/ValidationError";
import {DataGrid} from "@mui/x-data-grid";
import Link from "@mui/material/Link";
import Select from "react-select";
import {focalPersonColumnModel, recipientColumnModel} from "../statements/table-models";
import Prompt from "../../../customUtil/Prompt";

const GrantDetailForm = () => {
    const methods = useForm();
    const {handleSubmit, control, setValue, errors} = methods;

    const [data, setData] = useState({});
    const [countries, setCountries] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [peopleIds, setPeopleIds] = useState([]);
    const [primaryRecipients, setPrimaryRecipients] = useState([]);
    const [allFocalPointsSelected, setAllFocalPointsSelected] = useState(false);
    const [allPrContactsSelected, setAllPrContactsSelected] = useState(false);
    const [changeRecipient, setChangeRecipient] = useState(false);
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isReady, setIsReady] = useState(false);
    const [dirty, setDirty] = useState(false);
    const {id} = useParams();
    const history = useHistory();
    const [block, setBlock] = useState(false);  
    const refSubmitButtom = useRef();
    const [reportingGroups, setReportingGroups] = useState([]);
    
    const [triggerExit, setTriggerExit] = useState({
        onOk: false,
        path: ""
      });
      const [isVisibleDialog, setVisibleDialog] = useState(false);

    const handleGoToIntendedPage = useCallback(
        (location) => history.push(location),
        [history]
      );
      
      useEffect(() => {       
        const unblock = history.block((location) => {
          if (location.pathname !== `/gas/account-statements/${id}`) {
            if(block){
                setVisibleDialog(true);   
                setTriggerExit({...triggerExit, path: location.pathname});
            }else{
                setVisibleDialog(false);   
                setTriggerExit({onOk: true, path: location.pathname});
            }
                           
              }
         
          if (triggerExit.onOk) {
            return true;
          }
          return false;
        });
        if (triggerExit.onOk) {
            handleGoToIntendedPage(triggerExit.path);
          }
        return () => {
          unblock();
        };
      }, [history, triggerExit.onOk, triggerExit.path, block]);

    // DO THIS ONLY Once
    useEffect(() => {
        PrincipalRecipientsService.getAll().then((response) => {
            setPrimaryRecipients(response.data);
        });
    }, []);

    useEffect(() => {
        GasCountryService.getAll().then((response) => {
            setCountries(response.data);
        })
        EmailTemplateService.getAll().then((response) => {
            setEmailTemplates(response.data);
        })
        LanguageService.getAll().then((response) => {
            setLanguages(response.data);
        })
        ReportingGroupService.getAll().then((response) => {
            setReportingGroups(response.data);
        })

        if (id !== "new") {
            GrantDetailService.getById(id).then((response) => {
                setData(response.data);
                setIsReady(true);
            });
        } else {
            setData({gasCountry: {countryPeople: []}, emailTemplate: {}, language: {}, emailPeople: [], reportingGroup:{}});
            setIsReady(true);
        }
    }, [id, setValue]);


    useEffect(() => {
        if (data.emailPeople) {
            setPeopleIds(data.emailPeople.map(e => e.person.id))
        }
    }, [data])

    const checkIfIncluded = (id) => {
        return peopleIds.includes(id);
    }

    const removeAll = (rows) => {
        let newEmailPeople = data.emailPeople;
        rows.forEach(row => {
            newEmailPeople = newEmailPeople.filter(p => p.person.id !== row.id);
        });
        setData({...data, emailPeople: newEmailPeople});
        setDirty(true);
    }

    const addRemove = (row, add) => {
        let newEmailPeople = data.emailPeople;
        if (!add) {
            newEmailPeople = newEmailPeople.filter(p => p.person.id !== row.id);
        } else if (add) {
            newEmailPeople.push({person: row});
        }

        setData({...data, emailPeople: newEmailPeople});
        setDirty(true);
    }

    const toggleSelectAllFocalPoints = () => {
        if (allFocalPointsSelected) {
            removeAll(data.gasCountry.countryPeople.map(p => p.person));
        } else {
            data.gasCountry.countryPeople.forEach((p) => addRemove(p.person, !allFocalPointsSelected))
        }

        setAllFocalPointsSelected(!allFocalPointsSelected);
        setDirty(true);
    }

    const toggleSelectAllPRs = () => {
        if (allPrContactsSelected) {
            removeAll(data.principalRecipient.programContacts.map(p => p.contact));
        } else {
            data.principalRecipient.programContacts.forEach((p) => addRemove(p.contact, !allPrContactsSelected))
        }
        setAllPrContactsSelected(!allPrContactsSelected);
        setDirty(true);
    }

    const onPrincipalRecipientChanged = (r) => {
        //find the recipient based on id.
        const recipient = primaryRecipients.find(p => p.id === r);
        //remove the emails from the old recipient
        if (data.principalRecipient) {
            removeAll(data.principalRecipient.programContacts.map(p => p.contact));
        }
        //add the new recipient
        setData({...data, principalRecipient: recipient});
        setChangeRecipient(false);
        setDirty(true);
    }

    const onCheckedChanged = (row) => {
        let newEmailPeople = data.emailPeople;
        if (checkIfIncluded(row.id)) {
            newEmailPeople = newEmailPeople.filter(p => p.person.id !== row.id);
        } else {
            newEmailPeople.push({person: row});
        }
        setData({...data, emailPeople: newEmailPeople});
        setDirty(true);
    }

    const formatRecipient = (r) => {
        let name = "";
        if (r.gasCountry.code) {
            name = r.gasCountry.code + " - ";
        } else {
            name = r.gasCountry.name + " - ";
        }
        name += r.diseases + ' - ' + r.grant;
        return name;
    }

    const onCountryChanged = (country) => {
        var c = countries.filter((d) => d.id === country)[0];
        setData({...data, gasCountry: c});
    }

    const validateContacts = () => {
        GrantDetailService.validateContacts(data.id)
            .then((response) => {
                setMessage('Contacts Validated for this Grant');
                setData(response.data);
            });
    }

    const onSubmit = d => {
        setBlock(false);
        setVisibleDialog(false);
        const request = {
            ...data, ...d,
            gasCountry: {id: d.gasCountry},
            language: {id: d.language},
            emailTemplate: {id: d.emailTemplate},
            reportingGroup: {id: d.reportingGroup}
        }
        if (data.id) {
            GrantDetailService.update(request)
                .then((response) => {
                    setData(response.data);
                    setMessage('Grant Detail updated.');
                    setDirty(false);
                }).catch((error) => {
                setErrorMessage(error.message);
            });
        } else {
            GrantDetailService.create(request).then((response) => {
                if (!response.data.error) {
                    setData(response.data);
                    setDirty(false);
                    setMessage('Grant Detail Created.');
                }
            });
        }
    };

    if (!isReady) {
        return (
            <CircularProgress/>
        );
    }
    const triggerSubmit = () => {
        refSubmitButtom?.current?.click();
      };     
   
      const onSave = (event) => {
          console.log(event)
          triggerSubmit();
          setBlock(false);
          setVisibleDialog(false);
      }
  
      const checkModified = (event) => {
          if(event.type ===  "change"){
              setBlock(true);
                 }
              }
    return (
        <Container>
            <header>
                <h1>Grant Detail</h1>
            </header>
            {
                message && <Alert severity="success">{message}</Alert>
            }
            {
                errorMessage && <Alert severity="error">{errorMessage}</Alert>
            }
            <form style={{textAlign: 'left'}} onSubmit={handleSubmit(onSubmit)} onChange={event => {checkModified(event)}}>
            <Prompt
               visible={isVisibleDialog}
               onClose={() => setVisibleDialog(false)}
               onOk={() => {
               setTriggerExit({...triggerExit, onOk: true});
               setVisibleDialog(false);
               setBlock(false);
            }}
               onSave={onSave}
            />
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <p>
                            Create / update grant details.
                        </p>
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="grantName"
                            control={control}
                            fullWidth
                            as={<TextField></TextField>}
                            label="Name"
                            color="primary"
                            variant="outlined"
                            rules={{required: 'Name is required.'}}
                            defaultValue={data.grantName}
                        />
                        <ValidationError error={errors.grantName}/>
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="prsContact"
                            control={control}
                            fullWidth
                            as={<TextField></TextField>}
                            label="Primary Prs Contact"
                            color="primary"
                            variant="outlined"
                            disabled
                            defaultValue={data.gasCountry && data.gasCountry.primaryPrs && data.gasCountry.primaryPrs.person.email}
                        />
                    </Grid>
                    {/*<Grid item xs={6}>*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormControlLabel label="Country" variant="outlined" labelPlacement="top" control={<></>}/>
                        <Controller
                            name="gasCountry"
                            control={control}
                            label="Country"
                            color="primary"
                            rules={{required: 'Country is required.'}}
                            variant="outlined"
                            defaultValue={data.gasCountry && data.gasCountry.id}
                            render={(
                                {onChange, onBlur, value, name, ref},
                                {invalid, isTouched, isDirty}
                            ) => (
                                <Select
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    options={countries.map(r => ({value: r.id, label: r.name}))}
                                    value={countries.map(r => ({
                                        value: r.id,
                                        label: r.name
                                    })).filter(c => value === c.value)}
                                    onChange={val => {
                                        onChange(val.value);
                                        onCountryChanged(val.value);
                                    }}
                                    inputRef={ref}
                                />
                            )}
                        />
                        <ValidationError error={errors.gasCountry}/>
                    </Grid>
                    {/*<Grid item xs={6}>*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormControlLabel label="Language" variant="outlined" labelPlacement="top" control={<></>}/>
                        <Controller
                            name="language"
                            control={control}
                            label="Language"
                            rules={{required: 'Language is required.'}}
                            color="primary"
                            variant="outlined"
                            defaultValue={data.language && data.language.id}
                            render={(
                                {onChange, onBlur, value, name, ref},
                                {invalid, isTouched, isDirty}
                            ) => (
                                <Select

                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    options={languages.map(r => ({value: r.id, label: r.name}))}
                                    value={languages.map(r => ({
                                        value: r.id,
                                        label: r.name
                                    })).filter(c => value === c.value)}
                                    onChange={val => onChange(val.value)}
                                    inputRef={ref}
                                />
                            )}
                        />
                        <ValidationError error={errors.language}/>
                    </Grid>
                    {/*/!*<Grid item xs={6}>*!/*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormControlLabel label="Email Template" variant="outlined" labelPlacement="top"
                                          control={<></>}/>
                        <Controller
                            name="emailTemplate"
                            control={control}
                            label="Email Template"
                            rules={{required: 'Email Template is required.'}}
                            color="primary"
                            variant="outlined"
                            defaultValue={data.emailTemplate && data.emailTemplate.id}
                            render={(
                                {onChange, onBlur, value, name, ref},
                                {invalid, isTouched, isDirty}
                            ) => (
                                <Select

                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    options={emailTemplates.map(r => ({value: r.id, label: r.templateName}))}
                                    value={emailTemplates.map(r => ({
                                        value: r.id,
                                        label: r.templateName
                                    })).filter(c => value === c.value)}
                                    onChange={val => onChange(val.value)}
                                    inputRef={ref}
                                />
                            )}
                        />
                        <ValidationError error={errors.emailTemplate}/>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel label="Reporting Group" variant="outlined" labelPlacement="top"
                                          control={<></>}/>
                        <Controller
                            name="reportingGroup"
                            control={control}
                            label="Reporting Group"
                            rules={{required: 'Reporting Group is required.'}}
                            color="primary"
                            variant="outlined"
                            defaultValue={data.reportingGroup && data.reportingGroup.id}
                            render={(
                                {onChange, onBlur, value, name, ref},
                                {invalid, isTouched, isDirty}
                            ) => (
                                <Select

                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    options={reportingGroups.map(r => ({value: r.id, label: r.name}))}
                                    value={reportingGroups.map(r => ({
                                        value: r.id,
                                        label: r.name
                                    })).filter(c => value === c.value)}
                                    onChange={val => onChange(val.value)}
                                    inputRef={ref}
                                />
                            )}
                        />
                        <ValidationError error={errors.reportingGroup}/>
                    </Grid>
                    {/*<Grid item xs={6}>*/}
                    {/*</Grid>*/}
                    <Grid item xs={6}>
                        <FormControlLabel label="Active" variant="outlined" control={
                            <Controller
                                name="active"
                                control={control}
                                color="primary"
                                variant="outlined"
                                defaultValue={data.active}
                                render={(
                                    {onChange, onBlur, value, name, ref},
                                    {invalid, isTouched, isDirty}
                                ) => (
                                    <Checkbox
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.checked)}
                                        checked={value}
                                        inputRef={ref}
                                    />
                                )}
                            />
                        }/>
                    </Grid>
                    <Grid item xs={6}>
                    </Grid>

                    <Grid item xs={12}>
                        Focal People in Country
                        <DataGrid
                            rowHeight={35}
                            autoHeight={true}
                            rows={data.gasCountry ? data.gasCountry.countryPeople : []}
                            columns={focalPersonColumnModel(checkIfIncluded, onCheckedChanged, toggleSelectAllFocalPoints, allFocalPointsSelected)}
                            pageSize={20}/>
                    </Grid>

                    {
                        (!data.principalRecipient || changeRecipient) &&
                        <>
                            <Grid item xs={3}>
                                Select Principal Recipient
                            </Grid>
                            <Grid item xs={9}>
                                <Select onChange={value => onPrincipalRecipientChanged(value.value)}
                                        options={primaryRecipients.map(p => ({
                                            value: p.id,
                                            label: formatRecipient(p)
                                        }))}/>
                            </Grid>
                        </>
                    }
                    {
                        data.principalRecipient &&
                        <Grid item xs={12}>
                            Principal Recipient Contacts {data.principalRecipient && <b>{formatRecipient(data.principalRecipient)}</b> }(<Link onClick={() => setChangeRecipient(true)}>change</Link>)
                            <DataGrid
                                rowHeight={35}
                                autoHeight={true}
                                rows={data.principalRecipient && data.principalRecipient.programContacts ? data.principalRecipient.programContacts : []}
                                columns={recipientColumnModel(checkIfIncluded, onCheckedChanged, toggleSelectAllPRs, allPrContactsSelected)}
                                pageSize={20}/>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" type="submit">Submit</Button>
                        &nbsp;
                        <Button disabled={!data.id || data.emailsValidated || dirty} variant="contained" color="secondary"
                                type="button" onClick={() => validateContacts()}>Validate Contacts</Button>
                    </Grid>
                </Grid>
                <button hidden={true} ref={refSubmitButtom} type={"submit"} />
            </form>
        </Container>
    );
};

export default GrantDetailForm;
